import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DesktopWindows, PhoneIphone } from '@mui/icons-material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import dayjs from 'dayjs';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { ControlledFormDateTimePicker } from 'src/shared/components/form/controlledDateTimePicker';
import { GroupMessageEmailTemplateView } from './groupMessageEmailTemplateView';
import { SendMessageSummary } from './sendMessageSummary';

type SendMessageModalProps = {
  open: boolean;
  mode: 'now' | 'schedule';
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
  // Mottagar-etikett från parent (sätts från selection-state, eftersom form-fältet inte alltid har selectionName).
  recipientsLabel?: string;
  // Antal befintliga bilagor (server-side documentFiles) som ska räknas in i sammanfattningen.
  existingAttachmentsCount?: number;
};

type ViewMode = 'desktop' | 'mobile';

const MOBILE_VIEWPORT_WIDTH = 390;

// Modal som visar utskickets sammanfattning + förhandsgranskning innan användaren bekräftar skickning eller schemaläggning.
export const SendMessageModal = (props: SendMessageModalProps) => {
  const { open, mode, onClose, onConfirm, loading, recipientsLabel, existingAttachmentsCount } = props;
  const { t } = useTranslation(['groupMessage', 'common']);
  const { watch } = useFormContext();
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');

  // Hämta värden live från formuläret så att modalen alltid visar senaste inmatningen.
  const body = (watch('body') as string | undefined) ?? '';
  const sendDate = watch('sendDate') as string | null | undefined;

  // Validera att vald tidpunkt ligger i framtiden (för schemaläggningsläget).
  const isScheduleMode = mode === 'schedule';
  const scheduledIsFuture = sendDate ? dayjs(sendDate).isAfter(dayjs()) : false;
  const scheduleValidationError = isScheduleMode && (!sendDate || !scheduledIsFuture);

  const handleConfirm = async () => {
    if (isScheduleMode && scheduleValidationError) return;
    await onConfirm();
  };

  const dialogTitle = isScheduleMode ? t('groupMessage:create.confirmSchedule') : t('groupMessage:create.confirmSendNow');

  const dialogActions = (
    <>
      <Button variant='outlined' color='primary' disableElevation onClick={onClose}>
        {t('common:button.cancel')}
      </Button>
      <LoadingButton
        loading={loading}
        variant='contained'
        color='primary'
        disableElevation
        startIcon={isScheduleMode ? <ScheduleIcon aria-hidden /> : <SendIcon aria-hidden />}
        onClick={handleConfirm}
        disabled={isScheduleMode && scheduleValidationError}
      >
        {isScheduleMode ? t('groupMessage:create.scheduleAction') : t('groupMessage:create.sendNow')}
      </LoadingButton>
    </>
  );

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      onSave={handleConfirm}
      maxWidth='lg'
      padding={0}
      dialogTitle={dialogTitle as string}
      dialogActions={dialogActions}
      dialogContent={
        <Grid container>
          {/* Vänster: sammanfattning + ev. datetime-picker */}
          <Grid item xs={12} lg={5} sx={{ p: 3, borderRight: { lg: 1 }, borderColor: { lg: 'divider' } }}>
            {isScheduleMode && (
              <Box sx={{ mb: 3 }}>
                <ControlledFormDateTimePicker name='sendDate' required label={t('groupMessage:create.scheduleSendTime') as string} />
                {scheduleValidationError && (
                  <Typography variant='caption' color='error' sx={{ mt: 0.5, display: 'block' }}>
                    {t('groupMessage:create.scheduleMustBeFuture')}
                  </Typography>
                )}
              </Box>
            )}
            <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
              {t('groupMessage:create.summary.title')}
            </Typography>
            <SendMessageSummary recipientsLabel={recipientsLabel} existingAttachmentsCount={existingAttachmentsCount} />
          </Grid>

          {/* Höger: live preview med Dator/Mobil-tabs */}
          <Grid item xs={12} lg={7} sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: '50vh', lg: '70vh' } }}>
            <Tabs
              value={viewMode}
              onChange={(_, value: ViewMode) => setViewMode(value)}
              variant='fullWidth'
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value='desktop'
                icon={<DesktopWindows fontSize='small' />}
                iconPosition='start'
                label={t('groupMessage:emailTemplates.previewDesktop')}
              />
              <Tab
                value='mobile'
                icon={<PhoneIphone fontSize='small' />}
                iconPosition='start'
                label={t('groupMessage:emailTemplates.previewMobile')}
              />
            </Tabs>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch',
                bgcolor: (theme) => theme.palette.grey[100],
                p: viewMode === 'mobile' ? 3 : 0,
                overflow: 'auto',
              }}
            >
              {viewMode === 'desktop' ? (
                <Box sx={{ width: '100%', height: '100%' }}>
                  <GroupMessageEmailTemplateView body={body} height='100%' />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: MOBILE_VIEWPORT_WIDTH,
                    maxWidth: '100%',
                    height: '100%',
                    borderRadius: 4,
                    border: '8px solid',
                    borderColor: 'grey.800',
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                    boxShadow: 3,
                  }}
                >
                  <GroupMessageEmailTemplateView body={body} height='100%' />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      }
    />
  );
};
